import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAppSelector } from '../../app/hooks';
import { getAllUsers } from '../../store/investor/apiHelpers';
import { CompanySideHeaders } from '../../modules/Investor/ManageUser/ManageUserConstants';
import { updateQueryParams } from '../../utils/searchParamUtils';
import { USER_TYPE } from '../ManageDealDrawer/DealDrawerConstants';

export type Headers = {
    id: number;
    label: string;
    type: 'static' | 'dynamic';
    key?: string;
}[];

export type TeamMember = {
    actionCta: string[];
    userId: string;
    name: string;
    emailId: string;
    mobileNumber: string;
    userType: string;
    userStatus: string;
    organizationName: string;
    organizationDisplayName: string;
    additionalDetails: { [key: string]: string };
    partnerOrgId: string;
    organizationId: string;
    customFields: { [key: string]: any };
};
export const useManageTeam = (orgId?: string, userType?: string) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [headers] = useState<Headers>(CompanySideHeaders);
    const [rows, setRows] = useState<TeamMember[]>([]);
    const [isRowsLoading, setIsRowsLoading] = useState(true);
    const [pageNo, setPageNo] = useState(1);
    const [showAddUserModal, setShowAddUserModal] = useState(false);
    const [currentRowUser, setCurrentRowUser] = useState<any>(null);

    const userData = useAppSelector((state) => state.user.userData);

    const fetchTeamData = async (pageNumber: number) => {
        try {
            setIsRowsLoading(true);
            const users = await getAllUsers({
                pageSize: 20,
                pageNumber,
                orgId: orgId || userData?.assesseeOrgId,
                userType: [userType],
            });

            if (users?.userDetails) {
                const mappedUsers = users.userDetails.map((user: any) => ({
                    ...user,
                    customFields: user.customFields || {},
                }));
                setRows(mappedUsers);
            }
        } catch (error) {
            console.error('Failed to fetch team members:', error);
            toast.error('Failed to fetch team members');
            setRows([]);
        } finally {
            setIsRowsLoading(false);
        }
    };

    const handleAddUser = (user: any) => {
        setCurrentRowUser({
            organizationId: orgId || userData?.assesseeOrgId,
            userType: userType,
            userId: '',
            name: '',
            emailId: '',
            mobileNumber: '',
            userStatus: '',
        });
        setShowAddUserModal(true);
    };

    const initializeData = () => {
        fetchTeamData(pageNo);

        if (!searchParams.get('page')) {
            updateQueryParams(searchParams, setSearchParams, { page: '1' }, location);
            setPageNo(1);
        } else {
            setPageNo(Number(searchParams.get('page')));
        }
    };

    useEffect(() => {
        initializeData();
    }, [pageNo, orgId, userType]);

    return {
        headers,
        rows,
        isRowsLoading,
        pageNo,
        showAddUserModal,
        currentRowUser,
        setPageNo,
        setShowAddUserModal,
        setCurrentRowUser,
        fetchTeamData,
        handleAddUser,
    };
};
